/**
 * When a Main Command message was sent, said in the owner's own locale (#651).
 *
 * Every time here is a record's own durable created_at - owner_commands, plans,
 * observer_messages, chat_messages. Nothing is minted on the client: a message
 * whose record carries no time (an owner row older than the column) has none,
 * and says none. Presentation only - order, archive boundaries and authority
 * never read it.
 *
 * Pure over the stored value, the clock and the locale, so a reload or a fast
 * refresh of the same row shows the same time.
 */

export interface MessageTime {
  /** The stored instant, normalized for <time dateTime>. */
  readonly iso: string;
  /** Compact: "7:24 PM" today, "Yesterday, 11:58 PM", "Sep 22, 7:24 PM", "Sep 22, 2025, 7:24 PM". */
  readonly label: string;
  /** Full local date and time, for hover and screen readers. */
  readonly full: string;
}

export interface MessageTimeOptions {
  /** Defaults to the browser's locale. */
  readonly locale?: string | readonly string[];
  /** Defaults to the browser's time zone. */
  readonly timeZone?: string;
}

/** The instant a stored value names, or null when it names none. */
export function messageTimeMs(stored: string | null | undefined): number | null {
  if (typeof stored !== 'string' || stored.trim() === '') return null;
  const ms = Date.parse(stored);
  return Number.isFinite(ms) ? ms : null;
}

/** Calendar day of an instant in the zone, as "YYYY-MM-DD". */
function dayKey(ms: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(ms);
}

/** The calendar day before a "YYYY-MM-DD" key. */
function dayBefore(key: string): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

function capitalized(text: string, locales: string[] | undefined): string {
  return text.charAt(0).toLocaleUpperCase(locales) + text.slice(1);
}

/**
 * The label and full text for one stored time, or null when the record has
 * none. Today reads as a time; yesterday says so; older days carry their date
 * (and year, when it is not this year), so history across midnight stays clear.
 */
export function messageTime(
  stored: string | null | undefined,
  nowMs: number,
  options: MessageTimeOptions = {},
): MessageTime | null {
  const ms = messageTimeMs(stored);
  if (ms === null) return null;
  const locales = options.locale === undefined
    ? undefined
    : [...(typeof options.locale === 'string' ? [options.locale] : options.locale)];
  const timeZone = options.timeZone;
  const clock = new Intl.DateTimeFormat(locales, { timeZone, hour: 'numeric', minute: '2-digit' }).format(ms);
  const day = dayKey(ms, timeZone);
  const today = Number.isFinite(nowMs) ? dayKey(nowMs, timeZone) : day;
  let label: string;
  if (day === today) {
    label = clock;
  } else if (day === dayBefore(today)) {
    const yesterday = new Intl.RelativeTimeFormat(locales, { numeric: 'auto' }).format(-1, 'day');
    label = `${capitalized(yesterday, locales)}, ${clock}`;
  } else {
    label = new Intl.DateTimeFormat(locales, {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: day.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(ms);
  }
  const full = new Intl.DateTimeFormat(locales, { timeZone, dateStyle: 'full', timeStyle: 'short' }).format(ms);
  return { iso: new Date(ms).toISOString(), label, full };
}
